import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Save, X, Plane, Search } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AddAircraftModal = ({ isOpen, onClose, onAircraftAdded }) => {
  const [loading, setLoading] = useState(false);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [typeSuggestions, setTypeSuggestions] = useState([]);
  const [showTypeSuggestions, setShowTypeSuggestions] = useState(false);
  const [searchingTypes, setSearchingTypes] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm({
    defaultValues: {
      engine_type: 'piston',
      complex: false,
      high_performance: false,
      retractable_gear: false,
      constant_speed_prop: false,
      glass_cockpit: false
    }
  });

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  // Load aircraft types on component mount
  useEffect(() => {
    if (isOpen) {
      const loadAircraftTypes = async () => {
        try {
          const response = await axios.get('/api/aircraft-types', getAuthHeaders());
          setAircraftTypes(response.data);
        } catch (error) {
          console.error('Error loading aircraft types:', error);
        }
      };
      loadAircraftTypes();
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
      setTypeSuggestions([]);
      setShowTypeSuggestions(false);
    }
  }, [isOpen, reset]);

  // Search aircraft types as user types
  const searchAircraftTypes = async (query) => {
    if (!query || query.length < 1) {
      setTypeSuggestions([]);
      setShowTypeSuggestions(false);
      return;
    }

    setSearchingTypes(true);
    try {
      // Filter aircraft types based on the query
      const filtered = aircraftTypes.filter(type =>
        type.type_code?.toLowerCase().includes(query.toLowerCase()) ||
        type.manufacturer?.toLowerCase().includes(query.toLowerCase()) ||
        type.model?.toLowerCase().includes(query.toLowerCase())
      );
      
      setTypeSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions
      setShowTypeSuggestions(true);
    } catch (error) {
      console.error('Error searching aircraft types:', error);
      setTypeSuggestions([]);
    } finally {
      setSearchingTypes(false);
    }
  };

  // Handle aircraft type selection
  const selectAircraftType = (aircraftType) => {
    // Set the aircraft type
    setValue('aircraft_type', aircraftType.type_code);
    setValue('aircraft_type_id', aircraftType.id); 
    
    // Auto-populate manufacturer and model
    if (aircraftType.manufacturer) {
      setValue('manufacturer', aircraftType.manufacturer);
    }
    if (aircraftType.model) {
      setValue('model', aircraftType.model);
    }
    
    // Auto-populate engine type based on database value
    if (aircraftType.engine_type) {
      // Map database engine types to form values
      const engineTypeMapping = {
        'Jet': 'turbojet',
        'Turboprop': 'turboprop', 
        'Piston': 'piston',
        'Electric': 'electric'
      };
      
      const mappedEngineType = engineTypeMapping[aircraftType.engine_type] || 
                              aircraftType.engine_type.toLowerCase();
      setValue('engine_type', mappedEngineType);
    }
    
    // Auto-populate characteristics if available
    if (aircraftType.complex !== undefined) {
      setValue('complex', aircraftType.complex);
    }
    if (aircraftType.high_performance !== undefined) {
      setValue('high_performance', aircraftType.high_performance);
    }
    if (aircraftType.retractable_gear !== undefined) {
      setValue('retractable_gear', aircraftType.retractable_gear);
    }
    if (aircraftType.constant_speed_prop !== undefined) {
      setValue('constant_speed_prop', aircraftType.constant_speed_prop);
    }
    if (aircraftType.glass_cockpit !== undefined) {
      setValue('glass_cockpit', aircraftType.glass_cockpit);
    }
    
    // Hide suggestions
    setShowTypeSuggestions(false);
    setTypeSuggestions([]);
    
    // Show feedback about what was auto-populated
    const autoPopulatedFields = [];
    if (aircraftType.manufacturer) autoPopulatedFields.push('manufacturer');
    if (aircraftType.model) autoPopulatedFields.push('model');
    if (aircraftType.engine_type) autoPopulatedFields.push('engine type');
    
    if (autoPopulatedFields.length > 0) {
      toast.success(`Auto-populated: ${autoPopulatedFields.join(', ')}`);
    }
  };

  // Hide suggestions when clicking outside
  const handleBlur = () => {
    // Small delay to allow click events on suggestions to fire
    setTimeout(() => {
      setShowTypeSuggestions(false);
    }, 150);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.post('/api/aircraft', data, getAuthHeaders());
      
      toast.success('Aircraft added successfully!');
      
      // Call the callback with the new aircraft data
      if (onAircraftAdded) {
        onAircraftAdded({
          aircraft_id: data.aircraft_id,
          aircraft_type: data.aircraft_type,
          manufacturer: data.manufacturer,
          model: data.model
        });
      }
      
      // Close modal and reset form
      onClose();
      reset();
    } catch (error) {
      console.error('Error adding aircraft:', error);
      if (error.response?.data?.error?.includes('already exists')) {
        toast.error('Aircraft with this tail number already exists');
      } else {
        toast.error('Failed to add aircraft');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModalClick = (e) => {
    // Prevent closing when clicking inside the modal content
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="aviation-gradient p-2 rounded-lg">
              <Plane className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Add New Aircraft</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Aircraft Information */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Aircraft Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tail Number / Aircraft ID *
                  </label>
                  <input
                    type="text"
                    {...register('aircraft_id', { 
                      required: 'Aircraft ID is required',
                      pattern: {
                        value: /^[A-Z0-9-]+$/i,
                        message: 'Aircraft ID should contain only letters, numbers, and hyphens'
                      }
                    })}
                    className="input-field"
                    placeholder="e.g., N12345, G-ABCD"
                    style={{ textTransform: 'uppercase' }}
                  />
                  {errors.aircraft_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.aircraft_id.message}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aircraft Type *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register('aircraft_type', { required: 'Aircraft type is required' })}
                      className="input-field pr-10"
                      placeholder="e.g., C172, PA28, B737"
                      onChange={(e) => {
                        setValue('aircraft_type', e.target.value);
                        searchAircraftTypes(e.target.value);
                      }}
                      onBlur={handleBlur}
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    
                    {/* Aircraft type suggestions dropdown */}
                    {showTypeSuggestions && (
                      <div className="absolute z-30 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto top-full mt-1">
                        {searchingTypes ? (
                          <div className="px-3 py-2 text-center text-gray-500">Searching...</div>
                        ) : typeSuggestions.length > 0 ? (
                          typeSuggestions.map((type) => (
                            <button
                              key={type.id}
                              type="button"
                              onClick={() => selectAircraftType(type)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-200 last:border-b-0 focus:outline-none focus:bg-gray-100"
                            >
                              <div className="font-medium">{type.type_code}</div>
                              <div className="text-sm text-gray-600">
                                {type.manufacturer} {type.model}
                                {type.engine_type && ` • ${type.engine_type}`}
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-center text-gray-500">
                            No matching aircraft types found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {errors.aircraft_type && (
                    <p className="mt-1 text-sm text-red-600">{errors.aircraft_type.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Start typing to see suggestions from database
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    {...register('manufacturer')}
                    className="input-field"
                    placeholder="e.g., Cessna, Piper, Boeing"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-populated when selecting aircraft type
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model
                  </label>
                  <input
                    type="text"
                    {...register('model')}
                    className="input-field"
                    placeholder="e.g., Skyhawk, Cherokee, 737-800"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-populated when selecting aircraft type
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year
                  </label>
                  <input
                    type="number"
                    {...register('year', {
                      min: { value: 1900, message: 'Year must be 1900 or later' },
                      max: { value: new Date().getFullYear() + 5, message: 'Year cannot be more than 5 years in the future' }
                    })}
                    className="input-field"
                    placeholder="e.g., 2010"
                    min="1900"
                    max={new Date().getFullYear() + 5}
                  />
                  {errors.year && (
                    <p className="mt-1 text-sm text-red-600">{errors.year.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Country
                  </label>
                  <select {...register('registration_country')} className="input-field">
                    <option value="">Select Country</option>
                    <option value="US">United States (N)</option>
                    <option value="CA">Canada (C)</option>
                    <option value="GB">United Kingdom (G)</option>
                    <option value="AU">Australia (VH)</option>
                    <option value="DE">Germany (D)</option>
                    <option value="FR">France (F)</option>
                    <option value="IT">Italy (I)</option>
                    <option value="JP">Japan (JA)</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Engine and Performance */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Engine & Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Engine Type
                  </label>
                  <select {...register('engine_type')} className="input-field">
                    <option value="piston">Piston</option>
                    <option value="turboprop">Turboprop</option>
                    <option value="turbojet">Turbojet</option>
                    <option value="electric">Electric</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-populated when selecting aircraft type
                  </p>
                </div>

                <div className="flex items-center space-x-2 mt-8">
                  <input
                    type="checkbox"
                    {...register('high_performance')}
                    className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-900">
                    High Performance (>200 HP)
                  </label>
                </div>
              </div>
            </div>

            {/* Aircraft Characteristics */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Aircraft Characteristics</h3>
              <p className="text-sm text-gray-600 mb-4">
                These will be auto-populated when selecting a known aircraft type from the database
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('complex')}
                      className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-900">
                      Complex Aircraft
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('retractable_gear')}
                      className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-900">
                      Retractable Landing Gear
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('constant_speed_prop')}
                      className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-900">
                      Constant Speed Propeller
                    </label>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('glass_cockpit')}
                      className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-900">
                      Glass Cockpit
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Additional Information</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="input-field"
                  placeholder="Additional notes about this aircraft, equipment, special procedures, etc."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end space-x-4 -mx-6 -mb-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex items-center space-x-2"
                disabled={loading}
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{loading ? 'Saving...' : 'Save Aircraft'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddAircraftModal;